import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '@/components';
import { Incident } from '@/services/api/incidents';
import { getIncidentById } from '@/services/api/incidents';
import { createMaintenance, CreateMaintenanceRequest } from '@/services/api/maintenances';
import { MaintenanceSeverity, MaintenanceReason } from '@/types/common';
import { extractApiErrorMessage, getUserEmail } from '@/utils';
import { showErrorToast, showSuccessToast } from '@/components/toast/globalToast';
import CreateMaintenanceModal from '@/pages/maintenance/components/CreateMaintenanceModal';
import IncidentDetailContent from './components/IncidentDetailContent';
import IncidentStatusTimeline from './components/IncidentStatusTimeline';

const IncidentDetailPage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const [incident, setIncident] = useState<Incident | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isMaintenanceModalOpen, setIsMaintenanceModalOpen] = useState(false);

  const fetchDetail = useCallback(async () => {
    if (!slug) return;
    setIsLoading(true);
    try {
      const data = await getIncidentById(slug);
      setIncident(data);
    } catch (error) {
      showErrorToast(
        'Error',
        extractApiErrorMessage(error, 'Failed to load incident detail')
      );
    } finally {
      setIsLoading(false);
    }
  }, [slug]);

  useEffect(() => {
    fetchDetail();
  }, [fetchDetail]);

  const initialMaintenanceFormData = useMemo(() => {
    if (!incident) return undefined;

    return {
      reason: MaintenanceReason.LINKED_TO_INCIDENT,
      incidentID: incident.ID || '',
      vehicleID: incident.vehicleID || '',
      driverID: incident.driverID || 0,
      driverCode: incident.driverCode || '',
      driverName: incident.driverName || '',
      issueDescription: '',
      maintenanceDate: '',
      severity: '' as unknown as MaintenanceSeverity | '',
      estimatedCost: 0,
      costResponsibility: incident.costResponsibility || '',
      clientID: incident.clientID || 0,
      clientName: incident.clientName || '',
      oemID: incident.oemID || '',
      vehicleImageURL: '',
      notes: '',
    };
  }, [incident]);

  const handleMaintenanceSubmit = useCallback(
    async (data: CreateMaintenanceRequest) => {
      const userEmail = getUserEmail();

      const createData: CreateMaintenanceRequest = {
        ...data,
        createdBy: userEmail,
      };

      await createMaintenance(createData);
      showSuccessToast('Success', 'Maintenance created successfully');
      setIsMaintenanceModalOpen(false);
      await fetchDetail();
    },
    [fetchDetail]
  );

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto"></div>
          <p className="mt-4 text-gray-600 font-medium">Loading incident details...</p>
        </div>
      </div>
    );
  }

  if (!incident) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center max-w-md mx-auto">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Incident Not Found</h1>
          <p className="text-gray-600 mb-6">
            The incident case with ID "{slug}" could not be found.
          </p>
          <Button onClick={() => navigate('/incidents')} variant="primary">
            &larr; Back to Incidents
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-full mx-auto bg-white px-6 py-5">
      <div className="mx-auto">
        {/* Header Section */}
        <div className="mb-4 flex flex-col md:flex-row md:items-start justify-between gap-4">
          <div className="flex items-start gap-3 flex-1 min-w-0">
            <button
              onClick={() => navigate('/incidents')}
              className="mt-0.5 inline-flex h-9 w-9 items-center justify-center rounded-full border border-gray-200 bg-white text-gray-600 hover:bg-gray-50"
              aria-label="Back"
            >
              <svg
                className="h-5 w-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 19l-7-7 7-7"
                />
              </svg>
            </button>
            <div>
              <h1 className="text-xl font-semibold text-gray-900">
                Incident Case {incident.code ? `#${incident.code}` : ''}
              </h1>
              <p className="mt-0.5 text-xs text-gray-500">
                Incident occurred on{' '}
                {incident.incidentDate
                  ? new Intl.DateTimeFormat('en-GB', {
                      day: '2-digit',
                      month: 'long',
                      year: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit',
                      hour12: false,
                    }).format(new Date(incident.incidentDate)).replace(':', '.')
                  : '-'}{'; '}
                Reported by {incident.createdBy || '-'}
              </p>
            </div>
          </div>
          
          <div className="flex flex-col sm:flex-row flex-wrap items-stretch sm:items-center sm:justify-end gap-2 mt-0.5 w-full md:w-auto shrink-0">
            {incident.handoverID && incident.handoverID !== '-' && incident.handoverStatus !== 'ACTIVE' && (
              <Button
                variant="secondary"
                className="bg-purple-50 text-purple-700 border-none hover:bg-purple-100 font-medium"
                onClick={() => {
                  navigate(`/handovers/${incident.handoverID}`);
                }}
              >
                {incident.handoverStatus === 'RETURNED' ? 'View Returned Handover' : 'View Handover'} <i className="ri-arrow-right-s-line ml-1 font-bold" />
              </Button>
            )}
            <Button
              variant="primary"
              className="bg-purple-700 text-white hover:bg-purple-800 font-medium"
              onClick={() => {
                if (incident.maintenanceID && incident.maintenanceID !== '-') {
                  navigate(`/maintenances/${incident.maintenanceID}`);
                } else if (
                  incident.linkedMaintenanceCode &&
                  incident.linkedMaintenanceCode !== '-'
                ) {
                  navigate(`/maintenances/${incident.linkedMaintenanceID}`);
                } else {
                  setIsMaintenanceModalOpen(true);
                }
              }}
            >
              {(incident.maintenanceID && incident.maintenanceID !== '-') || (incident.linkedMaintenanceCode && incident.linkedMaintenanceCode !== '-')
                 ? 'View Maintenance Ticket'
                 : 'Create Maintenance Ticket'}
              <i className="ri-arrow-right-s-line ml-1 font-bold" />
            </Button>
          </div>
        </div>

        {/* Content Section */}
        <div className="flex flex-col gap-2 lg:flex-row lg:justify-between">
          <div className="w-full basis-[60%] flex-shrink-0 min-w-0">
             <IncidentDetailContent incident={incident} />
          </div>
          <div className="w-full basis-[40%] flex-shrink-0 min-w-0">
             <IncidentStatusTimeline incident={incident} />
          </div>
        </div>
      </div>
      <CreateMaintenanceModal
        isOpen={isMaintenanceModalOpen}
        onClose={() => setIsMaintenanceModalOpen(false)}
        onSubmit={handleMaintenanceSubmit}
        clearFormOnClose={true}
        initialFormData={initialMaintenanceFormData}
        disableVehicleSelection={!!incident?.vehicleID}
        initialVehicleLabel={
          incident
            ? `${incident.platNomor || 'Unknown Plate'} - ${
                incident.oemName || 'Unknown OEM'
              } - ${incident.modelName || 'Unknown Model'}`
            : undefined
        }
        disableReason={true}
        disableIncidentSelection={true}
        disableCostResponsibility={true}
        initialIncidentLabel={
          incident
            ? `${incident.code || ''} - ${incident.platNomor || ''}`
            : undefined
        }
      />
    </div>
  );
};

export default IncidentDetailPage;
